import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listReactions, type ReactionPage } from '@peruselab/api-client/reactions';

interface ReactionPickerProps {
  readonly onSelect: (reactionId: string, reactionRevision: number) => void;
}

type ReactionPickerState =
  | { readonly state: 'loading' }
  | { readonly state: 'ready'; readonly result: ReactionPage }
  | { readonly state: 'error'; readonly message: string };

// #140: the third of Illustration's three pickers -- lists the user's own saved Reactions so one
// can be placed as a reactionIllustrationElement. Rows are textual only (no ReactionThumbnail per
// row) to avoid an N+1 depiction fetch across a paginated list; the mini-diagram only renders once,
// after the reaction is actually placed on the canvas.
export function ReactionPicker({ onSelect }: ReactionPickerProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [reactions, setReactions] = useState<ReactionPickerState>({ state: 'loading' });
  const requestSequence = useRef(0);

  const loadPage = useCallback(
    async (requestedPage: number) => {
      const requestId = requestSequence.current + 1;
      requestSequence.current = requestId;
      setReactions({ state: 'loading' });
      try {
        const result = await listReactions(requestedPage);
        if (requestSequence.current === requestId) setReactions({ state: 'ready', result });
      } catch (error) {
        if (requestSequence.current === requestId) {
          setReactions({
            state: 'error',
            message: error instanceof Error ? error.message : t('reactionPicker.listError')
          });
        }
      }
    },
    [t]
  );

  useEffect(() => {
    void loadPage(0);
  }, [loadPage]);

  const result = reactions.state === 'ready' ? reactions.result : null;

  return (
    <section className="isomer-picker" aria-labelledby="reaction-picker-heading">
      <h2 id="reaction-picker-heading">{t('reactionPicker.heading')}</h2>
      <p>{t('reactionPicker.subheading')}</p>
      {reactions.state === 'loading' ? <p className="isomer-picker-status">{t('reactionPicker.loading')}</p> : null}
      {reactions.state === 'error' ? (
        <p className="isomer-picker-status isomer-picker-error">{reactions.message}</p>
      ) : null}
      {result && result.content.length === 0 ? <p>{t('reactionPicker.noneYet')}</p> : null}
      {result && result.content.length > 0 ? (
        <ul className="isomer-picker-list">
          {result.content.map((reaction) => (
            <li key={reaction.id}>
              <button type="button" onClick={() => onSelect(reaction.id, reaction.documentRevision)}>
                {reaction.title || reaction.id}{' '}
                {t('reactionPicker.revisionSuffix', { revision: reaction.documentRevision })}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {result && result.totalPages > 1 ? (
        <div className="recent-drawings-pagination" aria-label={t('reactionPicker.paginationAriaLabel')}>
          <button
            type="button"
            onClick={() => {
              const nextPage = page - 1;
              setPage(nextPage);
              void loadPage(nextPage);
            }}
            disabled={page === 0 || reactions.state === 'loading'}
          >
            {t('reactionPicker.previous')}
          </button>
          <span>{t('reactionPicker.pageOf', { page: result.page + 1, totalPages: result.totalPages })}</span>
          <button
            type="button"
            onClick={() => {
              const nextPage = page + 1;
              setPage(nextPage);
              void loadPage(nextPage);
            }}
            disabled={page + 1 >= result.totalPages || reactions.state === 'loading'}
          >
            {t('reactionPicker.next')}
          </button>
        </div>
      ) : null}
    </section>
  );
}
